import React from "react";
import { motion } from "framer-motion";
import "./index.css";
import { Link, usePath } from "./router";
import { BlogIndex, BlogPost } from "./Blog";

const services = [
  {
    title: "Workloading Support",
    desc: "Know exactly how many hours your space needs and how many staff to hire."
  },
  {
    title: "Team vs. Zone Cleaning Strategy",
    desc: "We'll help you choose the method that fits your layout, team size, and goals."
  },
  {
    title: "Staffing Model Planning",
    desc: "Build realistic roles and schedules that match your daily cleaning needs."
  },
  {
    title: "DIY Tools & Templates",
    desc: "Use our Excel calculators to plan cleaning time by room type, frequency, and task."
  },
  {
    title: "Startup Guidance",
    desc: "Setting up a new cleaning operation? We'll help you do it right from day one."
  }
];

function SiteHeader() {
  return (
    <header className="site-header">
      <div className="container">
        <div className="header-inner">
          <Link to="/" className="site-logo">
            <h1>Osmonz <span>Services</span></h1>
            <p className="logo-tagline">Smart Cleaning Solutions for Small Business</p>
          </Link>
          <nav className="site-nav">
            <Link to="/#services">Services</Link>
            <Link to="/#why-us">Why Us</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/#contact">Contact</Link>
          </nav>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      &copy; 2025 Osmonz Services. All rights reserved.
    </footer>
  );
}

function HomePage() {
  return (
    <>
      {/* Welcome */}
      <section id="intro" className="section-welcome">
        <div className="container">
          <motion.div
            className="welcome-grid"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div>
              <p className="eyebrow">Welcome to Osmonz</p>
              <h2 className="welcome-heading">
                Cleaning operations, <em>built right</em> for small business
              </h2>
              <p className="welcome-body">
                Osmonz Services is built for the small business owner—whether you're running a local
                café, salon, dental office, or boutique gym. We understand that hiring big corporate
                consultants just isn't in the cards for most small teams. That's why we offer clear,
                affordable help with cleaning operations—from finding your ideal staffing model to
                choosing between team or zone cleaning.
              </p>
            </div>
            <div>
              <div className="welcome-card">
                <span className="stat-num">100%</span>
                <span className="stat-label">Small Business Focus</span>
                <div className="card-divider"></div>
                <ul className="card-features">
                  <li>Flat-fee pricing, no upsells</li>
                  <li>Tailored to your budget</li>
                  <li>Clear, no-nonsense guidance</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="section-services">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-header">
              <p className="eyebrow">What We Offer</p>
              <h2 className="section-title">Our Services</h2>
            </div>
            <div className="services-grid">
              {services.map((service, i) => (
                <motion.div
                  key={i}
                  className={`service-card${i === services.length - 1 ? " service-card--full" : ""}`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <span className="service-num">0{i + 1}</span>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-desc">{service.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA — full-bleed */}
      <motion.section
        className="section-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <div className="cta-inner">
            <div>
              <p className="eyebrow eyebrow--light">Free Resource</p>
              <h3 className="cta-heading">Workloading Calculator</h3>
              <p className="cta-sub">
                Download our Excel tool to estimate cleaning time by room type, frequency, and task.
              </p>
            </div>
            <a
              href="Housekeeping_Workloading_Calculator_with_Chart.xlsx"
              className="cta-btn"
            >
              Download Cleaning Time Estimator
            </a>
          </div>
        </div>
      </motion.section>

      {/* Why Us */}
      <section id="why-us" className="section-why">
        <div className="container">
          <motion.div
            className="why-grid"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <blockquote className="why-quote">
              "We don't serve Fortune 500 companies—we serve everyday business owners who need
              smart, no-nonsense cleaning guidance."
            </blockquote>
            <div>
              <div className="section-header">
                <p className="eyebrow">Our Difference</p>
                <h2 className="section-title">Why Osmonz?</h2>
              </div>
              <p className="why-body">
                Our services are flat-fee, simple, and built around small business realities. We
                don't upsell software, we just help you find the right fit—so your cleaning
                operation runs smoothly, even on a tight budget.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section-contact">
        <div className="container">
          <motion.div
            className="contact-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="contact-heading">Get in Touch</h2>
              <p className="contact-sub">Email us for a 20-minute free consultation.</p>
              <div className="contact-items">
                <div className="contact-item">
                  <span className="contact-label">Email</span>
                  <a href="mailto:info@osmonzservices.com" className="contact-value">
                    info@osmonzservices.com
                  </a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">Phone</span>
                  <a href="tel:+13212453795" className="contact-value">
                    321-245-3795
                  </a>
                </div>
              </div>
            </div>
            <div className="contact-box">
              <h3 className="contact-box-title">Free Consultation</h3>
              <p className="contact-box-body">
                No obligations, just honest guidance for your small business cleaning operation.
                Reach out and we'll schedule a 20-minute call.
              </p>
              <a href="mailto:info@osmonzservices.com" className="contact-box-btn">
                Get Started
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default function App() {
  const path = usePath().replace(/\/+$/, "") || "/";

  let page = <HomePage />;
  if (path === "/blog") page = <BlogIndex />;
  else if (path.startsWith("/blog/")) page = <BlogPost slug={path.slice("/blog/".length)} />;

  return (
    <>
      <SiteHeader />
      <main>{page}</main>
      <SiteFooter />
    </>
  );
}
